/**
 * «Bruk standardforslag» — den ene formen, for alle listene som har knappen (K-3).
 *
 * ------------------------------------------------------------------------------------------------
 * HVORFOR DETTE ER ÉN KONTRAKT OG IKKE TRE.
 *
 * K-3: «Vanlige forslag (Presse, Get-in, Bar …) ligger IKKE inne. Hver tom liste har knappen "Bruk
 * standardforslag", som fyller inn en typisk pakke som deretter kan redigeres.»
 *
 * Tre lister har knappen — akkrediteringstyper, scenetider og årgangens avdelinger — og de tre
 * spørsmålene er identiske: «fyll denne årgangens liste med den vanlige pakken, og bare det som
 * mangler». Tre nesten like forespørselsskjemaer ville vært tre steder å glemme å legge til
 * `editionId` den dagen en fjerde liste får knappen.
 *
 * Selve pakkene ligger i databasen (`app.apply_default_pack`, migrering 0258) og ikke her. Det er
 * prinsipp 9 tatt på ordet: hvilke ord en festival bruker på et get-in er DATA, og innholdet i
 * forslaget skal kunne endres uten at kontrakten mellom nettleseren og API-et endres.
 *
 * ------------------------------------------------------------------------------------------------
 * SVARET ER ET TALL, OG DET TALLET ER POENGET.
 *
 * Funksjonen er idempotent: den legger bare inn det som ikke finnes. Et svar uten `added` ville
 * gjort «du hadde alt alle sammen» og «du fikk fem nye» til det samme svaret, og toasten kunne
 * ikke sagt hva som skjedde.
 */
import { z } from 'zod';

export const applyDefaultPackRequestSchema = z
  .object({ editionId: z.uuid() })
  .meta({ id: 'ApplyDefaultPackRequest' });
export type ApplyDefaultPackRequest = z.infer<typeof applyDefaultPackRequestSchema>;

export const applyDefaultPackResponseSchema = z
  .object({ added: z.number().int().min(0) })
  .meta({ id: 'ApplyDefaultPackResponse' });
export type ApplyDefaultPackResponse = z.infer<typeof applyDefaultPackResponseSchema>;

/**
 * «HENT AVDELINGER FRA ORGANISASJONEN» MED FORHÅNDSVISNING (QA13 #29, grep C2).
 *
 * Knappen la bare til det som manglet og endret aldri et navn — heller ikke når organisasjonen hadde
 * gitt avdelingen nytt navn. Nå viser den først hva som vil skje: hvilke som er nye, og hvilke som
 * har fått nytt navn i organisasjonen. Et navn endres bare når noen har krysset av for det.
 */
export const departmentImportPreviewSchema = z
  .object({
    added: z.array(z.object({ key: z.string(), name: z.string(), parentName: z.string().nullable() })),
    renamed: z.array(
      z.object({ departmentId: z.uuid(), key: z.string(), from: z.string(), to: z.string() }),
    ),
  })
  .meta({ id: 'DepartmentImportPreview' });
export type DepartmentImportPreview = z.infer<typeof departmentImportPreviewSchema>;

export const copyDepartmentsFromOrganizationRequestSchema = z
  .object({
    editionId: z.uuid(),
    /** Årgangens avdelinger som skal få organisasjonens navn. Usendt: ingen navn endres. */
    renameIds: z.array(z.uuid()).max(200).optional(),
  })
  .meta({ id: 'CopyDepartmentsFromOrganizationRequest' });
export type CopyDepartmentsFromOrganizationRequest = z.infer<
  typeof copyDepartmentsFromOrganizationRequestSchema
>;

export const copyDepartmentsFromOrganizationResponseSchema = z
  .object({ added: z.number().int().min(0), renamed: z.number().int().min(0) })
  .meta({ id: 'CopyDepartmentsFromOrganizationResponse' });
export type CopyDepartmentsFromOrganizationResponse = z.infer<
  typeof copyDepartmentsFromOrganizationResponseSchema
>;
